import { FC } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import type { PurchaseLimitBehaviorType } from 'src/apis/dto/DTOProduct';
import type { DTOBadge, DTOSubProductContent } from 'src/apis/dto/DTOShopModule';
import ResizingText from 'src/components/ResizingText';
import { DiscountBadge, ShopBadgeLabel } from 'src/components/Shop/ShopLabels';
import { hoverAndActiveCss } from 'src/utils/styled';
import { useSubscriptionButtonState } from './useSubscriptionButtonState';
const SubscribeButton = styled.div<{
  $hidden?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  padding: 0 12px;
  border-radius: 360px;
  background-color: ${({
  theme
}) => theme.color.surface4};
  visibility: ${({
  $hidden
}) => $hidden ? 'hidden' : 'visible'};
`;
const SubscribeButtonText = styled(ResizingText)<{
  $disabled?: boolean;
}>`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${({
  theme,
  $disabled
}) => $disabled ? theme.color.content6 : theme.color.content11};
`;
const CardContainer = styled.div<{
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: ${({
  theme
}) => theme.color.surface3};
  border-radius: 16px;
  overflow: hidden;
  cursor: ${({
  $disabled
}) => $disabled ? 'default' : 'pointer'};

  ${({
  theme,
  $disabled
}) => !$disabled && hoverAndActiveCss(`
      ${SubscribeButton} {
        background-color: ${theme.color.surface6};
      }
    `)}
`;
const ImageWrapper = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({
  theme
}) => theme.color.surface4};
`;
const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const BadgeWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-block-end: 8px;
`;
const Title = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Description = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content8};
  margin: 0;
  margin-block-start: 2px;
  word-wrap: break-word;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  flex: 1;
`;
const ButtonWrapper = styled.div`
  margin-top: auto;
  padding-top: 16px;
`;
export interface SubscriptionProduct {
  moduleId: string;
  productName: string;
  iconUrl: string;
  productId: string;
  categoryId: string;
  content?: DTOSubProductContent;
  introductoryDisplayDiscountRate?: string;
  badge?: DTOBadge;
  purchaseLimitExceeded?: boolean;
  purchaseLimitBehavior?: PurchaseLimitBehaviorType;
}
interface Props {
  product: SubscriptionProduct;
  onClick: () => void;
}
const SubscriptionCard: FC<Props> = ({
  product,
  onClick
}) => {
  const {
    t
  } = useTranslation();
  const {
    productName,
    iconUrl,
    content,
    badge,
    introductoryDisplayDiscountRate
  } = product;
  const {
    textKey,
    isDisabled,
    isHidden: isButtonHidden
  } = useSubscriptionButtonState(product);
  const showShopBadge = !!badge?.text;
  const showDiscountBadge = !!introductoryDisplayDiscountRate;
  const showBadgeWrapper = showShopBadge || showDiscountBadge;
  return <CardContainer role='button' tabIndex={isDisabled || isButtonHidden ? -1 : 0} $disabled={isDisabled || isButtonHidden} onClick={isDisabled || isButtonHidden ? undefined : onClick} onKeyDown={e => {
    if (isDisabled || isButtonHidden) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }} data-sentry-element="CardContainer" data-sentry-component="SubscriptionCard" data-sentry-source-file="SubscriptionCard.tsx">
      <ImageWrapper data-sentry-element="ImageWrapper" data-sentry-source-file="SubscriptionCard.tsx">
        <ProductImage src={iconUrl} alt={productName} data-sentry-element="ProductImage" data-sentry-source-file="SubscriptionCard.tsx" />
      </ImageWrapper>
      <ContentWrapper data-sentry-element="ContentWrapper" data-sentry-source-file="SubscriptionCard.tsx">
        {showBadgeWrapper && <BadgeWrapper>
            {showShopBadge && <ShopBadgeLabel {...badge} />}
            {showDiscountBadge && <DiscountBadge>
                {showShopBadge ? '+ ' : ''}
                {t('common_discount_off', {
            percentage: introductoryDisplayDiscountRate
          })}
              </DiscountBadge>}
          </BadgeWrapper>}
        <Title data-sentry-element="Title" data-sentry-source-file="SubscriptionCard.tsx">{productName}</Title>
        {content?.description && <Description>{content.description}</Description>}
        <ButtonWrapper data-sentry-element="ButtonWrapper" data-sentry-source-file="SubscriptionCard.tsx">
          <SubscribeButton $hidden={isButtonHidden} data-sentry-element="SubscribeButton" data-sentry-source-file="SubscriptionCard.tsx">
            <SubscribeButtonText minFontSize={12} maxLines={1} $disabled={isDisabled} data-sentry-element="SubscribeButtonText" data-sentry-source-file="SubscriptionCard.tsx">
              {t(textKey)}
            </SubscribeButtonText>
          </SubscribeButton>
        </ButtonWrapper>
      </ContentWrapper>
    </CardContainer>;
};
export default SubscriptionCard;